import { isPackageBanner } from "@/features/flights/hooks/flightSearch/utils";
import type {
  FullOfferType,
  MappedFlightOffer,
} from "@/features/flights/types/types";

type FlightsOverviewItem = MappedFlightOffer["flightsOverview"][number];

/**
 * Collapses duplicate branded fare offers that represent the same physical
 * flight(s) but differ only in fare tier (upsellSupport: true).
 *
 * Strategy:
 * - Non-flight offers (PackageBannerV3) always pass through unchanged.
 * - Flight offers with upsellSupport !== true always pass through unchanged.
 * - For upsell offers, each flightsOverview item is checked against a set of
 *   already-seen flight keys. Items whose key has already been seen are removed.
 *   If all items are removed the offer is dropped entirely; otherwise the offer
 *   is kept with the filtered flightsOverview and its keys are marked as seen.
 *
 * @param offers - The flat list of offers to process.
 * @param getKey - Extracts the deduplication key from a flightsOverview item.
 *   Return undefined to treat the item as non-deduplicatable (always kept).
 */
export function deduplicateOffersByFare(
  offers: FullOfferType[],
  getKey: (fo: FlightsOverviewItem) => string | undefined,
): FullOfferType[] {
  const seenKeys = new Set<string>();
  const collapsed: FullOfferType[] = [];

  for (const offer of offers) {
    if (isPackageBanner(offer) || !offer.upsellSupport) {
      collapsed.push(offer);
      continue;
    }

    const filteredOverview = offer.flightsOverview.filter((fo) => {
      const key = getKey(fo);
      if (key === undefined) return true;
      if (seenKeys.has(key)) return false;

      seenKeys.add(key);
      return true;
    });

    if (filteredOverview.length === 0) continue;

    collapsed.push({ ...offer, flightsOverview: filteredOverview });
  }

  return collapsed;
}
